import { useEffect, useState } from 'react';

import { Button } from '@ekhodealer/ekho-common/rev';

import { TextCell } from '@common/components/TextCell';

interface CopyableButtonProps {
	/** The value to copy to clipboard */
	value: string;
	/** Optional text to display instead of the value */
	displayText?: string;
	/** If true, uses monospace font for code-like text */
	monospace?: boolean;
}

/**
 * CopyableButton component that displays a value as a ghost button
 * that copies to clipboard on click
 */
export const CopyableButton = ({ value, displayText, monospace = false }: CopyableButtonProps) => {
	const [isCopied, setIsCopied] = useState(false);

	useEffect(() => {
		if (!isCopied) return;

		const timer = setTimeout(() => {
			setIsCopied(false);
		}, 1000);

		return () => clearTimeout(timer);
	}, [isCopied]);

	const handleCopy = async () => {
		if (!value) return;

		try {
			await navigator.clipboard.writeText(value);
			setIsCopied(true);
		} catch {
			// Silent failure - copy unsuccessful
		}
	};

	const textStyle = monospace ? { fontFamily: 'monospace' } : undefined;

	return (
		<Button variant='ghost' onPress={handleCopy} size='small'>
			<div style={{ position: 'relative' }}>
				<div style={{ visibility: isCopied ? 'hidden' : 'visible', ...textStyle }}>
					<TextCell value={displayText || value} />
				</div>
				{isCopied && (
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}>
						<TextCell value='Copied!' />
					</div>
				)}
			</div>
		</Button>
	);
};
